import React, { useState, useCallback, useEffect, useRef } from 'react';
import { GoogleMap, useLoadScript, Marker, OverlayView } from '@react-google-maps/api';
import { courses } from '../config/courses';

// Debug log the imported courses
console.log('Imported courses:', courses);

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  position: 'relative'
};

const loadingContainerStyle = {
  width: '100%',
  height: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  backgroundColor: 'transparent',
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 0
};

// Center on Vancouver area
const center = {
  lat: 49.25,
  lng: -122.93
};
const defaultZoom = 10;

const libraries = ['places'];

const options = {
  mapTypeControl: true,
  streetViewControl: true,
  fullscreenControl: true,
  mapTypeId: 'roadmap',
  zoomControl: true,
  gestureHandling: 'greedy', // Makes the map immediately interactive
  clickableIcons: false, // Reduces loading time by disabling default POI clicks
};

const CourseMap = ({ selectedCourseIds, onCourseToggle }) => {
  const mapRef = useRef(null);
  const [showMap, setShowMap] = useState(false);
  const [mapSize, setMapSize] = useState({ width: 0, height: 0 });
  const [hoveredCourse, setHoveredCourse] = useState(null);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [isMapReady, setIsMapReady] = useState(false);

  // Debug log when component renders
  console.log('CourseMap rendered', window.innerWidth);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
    preventGoogleFontsLoading: true // Prevents loading unnecessary fonts
  });

  // Map the course IDs to match the COURSES object in SearchForm
  const courseIdMap = {
    'seymour': '8',
    'burnaby-mountain': '1',
    'riverway': '2',
    'langara': '3',
    'fraserview': '4',
    'mccleery': '5',
    'university': '6',
    'northlands': '7',
    'gleneagles': '9',
    'skyCourse': '10',
    'fineCourse': '11',
    'carnoustie': '12',
    'genorthcourse': '13',
    'gesouthcourse': '14',
    'surreygolfclub': '15',
    'surreywillows': '16',
    'savagecreek': '17',
    'mylora18': '18',
    'countrymeadows': '19',
    'furrycreek': '20',
    'meadowgardens': '21',
    'musqueam': '22',
    'hillsatportal': '23',
    'meridianhills': '24',
    'harrisonresort': '25',
    'thefalls': '26',
    'tsawwassensprings': '27',
    'sandpiperresort': '28',
    'hopegolf': '29',
    'greenteelangley': '30',
    'mountbrenton': '31',
    'arbutusridge': '32',
    'crownislepark': '33',
    'crownisleresort': '34',
    'mulligans': '35',
    'sunnydale': '36',
    'cowichan': '37',
    'duncanmeadows': '38',
    'marchmeadows': '39',
    'morningstar': '40',
    'saltspring': '41',
    'bearmountainmountain': '42',
    'bearmountainvalley': '43',
    'metchosin': '44',
    'bigsky': '45',
    'chateauwhistler': '46',
    'bighorn': '47',
    'eaglepoint': '48',
    'kamloopsgolf': '49',
    'mountpaul': '50',
    'michaelbrook': '51',
    'missioncreek': '52',
    'sunsetranch': '53',
    'towerranch': '54',
    'merrittgolf': '55',
    'predatorridge': '56',
    'spallumcheen': '57',
    'theriseresort': '58',
    'tobiano': '59',
    'talkingrock': '60',
    'sunpeaks': '61',
    'shuswapnational': '62',
    'summerland': '63',
    'fairviewmountain': '64',
    'nkmipcanyon': '65',
    'osoyoosdesertgold': '66',
    'osoyoosparkmeadows': '67',
    'revelstoke': '68',
    'princeton': '69',
    'storeycreek': '70',
    'qualicumbeach': '71',
    'blueocean': '72',
    'covelinks': '73',
    'northviewridge': '74',
    'northviewcanal': '75',
    'morgancreek': '76',
    'deltagolf': '77',
    'kingslinks': '78',
    'belmont': '79',
    'swanesetresort': '80',
    'swanesetlinks': '81',
    'hazelmere': '82',
    'redwoods': '83',
    'twoeagles': '84',
    'guildford': '85'
  };

  // Update markers when selectedCourseIds changes
  useEffect(() => {
    if (isLoaded && courses) {
      const newMarkers = courses.map(course => ({
        id: course.id,
        position: {
          lat: parseFloat(course.location.lat),
          lng: parseFloat(course.location.lng)
        },
        title: course.name,
        course: course,
        isSelected: selectedCourseIds?.includes(courseIdMap[course.id])
      }));
      setMarkers(newMarkers);
    }
  }, [isLoaded, selectedCourseIds]);

  // Handle container size changes
  useEffect(() => {
    if (!mapRef.current) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        setMapSize({ width, height });
      }
    });

    resizeObserver.observe(mapRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Delay map rendering on mobile
  useEffect(() => {
    const isMobile = window.innerWidth < 640;
    const delay = isMobile ? 300 : 0; // Longer delay for mobile
    
    const timeout = setTimeout(() => {
      setShowMap(true);
    }, delay);

    return () => clearTimeout(timeout);
  }, []);

  // Force Google Maps to resize after mount and size changes
  useEffect(() => {
    if (map && window.google && window.google.maps) {
      const resizeMap = () => {
        window.google.maps.event.trigger(map, 'resize');
        // Do NOT set center/zoom here!
      };

      // Initial resize
      resizeMap();

      // Additional resize after a delay on mobile
      if (window.innerWidth < 640) {
        setTimeout(resizeMap, 500);
      }
    }
  }, [map, mapSize]);

  const onLoad = useCallback((map) => {
    setMap(map);

    // Exclude Furry Creek from bounds
    const EXCLUDED_COURSE_IDS = ['furrycreek'];
    const includedCourses = courses.filter(
      course => !EXCLUDED_COURSE_IDS.includes(course.id)
    );

    if (includedCourses.length > 0 && window.google && window.google.maps) {
      const bounds = new window.google.maps.LatLngBounds();
      includedCourses.forEach(course => {
        bounds.extend({
          lat: parseFloat(course.location.lat),
          lng: parseFloat(course.location.lng)
        });
      });
      map.fitBounds(bounds);
    } else {
      // Fallback to default center/zoom
      map.setCenter(center);
      map.setZoom(defaultZoom);
    }

    setIsMapReady(true);
  }, []);

  const getPixelPositionOffset = (width, height) => ({
    x: -(width / 2),
    y: -height - 20
  });

  // Determine if on mobile
  const isMobile = window.innerWidth < 640;

  // Minimal map options for mobile
  const mobileMapOptions = {
    ...options,
    zoomControl: false,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    scaleControl: false,
  };

  if (loadError) {
    console.error('Error loading Google Maps:', loadError);
    return (
      <div style={loadingContainerStyle}>
        <div className="max-w-[400px] text-center">
          <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div>Error loading Google Maps. Please check your API key and internet connection.</div>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={loadingContainerStyle}>
        <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
        <div className="text-center text-red-500 mt-2">If you see this message and no map, the map failed to load.</div>
      </div>
    );
  }

  // Debug log before rendering markers
  console.log('About to render markers for courses:', courses);

  return (
    <div 
      ref={mapRef}
      style={{ 
        position: 'relative', 
        height: '100%',
        minHeight: isMobile ? '300px' : '560px',
        width: '100%', 
        overflow: 'hidden',
        backgroundColor: 'transparent'
      }}
    >
      {showMap && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1 }}>
          <GoogleMap
            mapContainerStyle={{
              width: '100%',
              height: '100%'
            }}
            zoom={defaultZoom}
            center={center}
            onLoad={onLoad}
            options={isMobile ? mobileMapOptions : {
              ...options,
              fullscreenControl: false,
              mapTypeControl: true,
              mapTypeControlOptions: {
                position: window.google.maps.ControlPosition.TOP_RIGHT
              },
              zoomControl: true,
              streetViewControl: false,
              scaleControl: true,
            }}
          >
            {isMapReady && (
              <>
                {markers.map(marker => (
                  <Marker
                    key={marker.id}
                    position={marker.position}
                    title={marker.title}
                    onMouseOver={() => setHoveredCourse(marker.course)}
                    onMouseOut={() => setHoveredCourse(null)}
                    onClick={() => onCourseToggle(courseIdMap[marker.id])}
                    icon={
                      marker.isSelected
                        ? {
                            url: '/assets/golfpin.png',
                            scaledSize: new window.google.maps.Size(32, 32),
                            anchor: new window.google.maps.Point(16, 32),
                          }
                        : {
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 8,
                            fillColor: '#9ca3af',
                            fillOpacity: 1,
                            strokeColor: '#14532d',
                            strokeWeight: 2,
                          }
                    }
                  />
                ))}

                {hoveredCourse && (
                  <OverlayView
                    position={hoveredCourse.location}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={getPixelPositionOffset}
                  >
                    <div
                      style={{
                        backgroundColor: 'white',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        fontSize: '12px',
                        fontWeight: '500',
                        whiteSpace: 'nowrap',
                        pointerEvents: 'none',
                        display: 'inline-block',
                        minWidth: 'fit-content',
                        color: '#1a1a1a',
                        position: 'relative',
                        transform: 'translateY(-100%)',
                        marginBottom: '-8px'
                      }}
                    >
                      {hoveredCourse.name}
                    </div>
                  </OverlayView>
                )}
              </>
            )}
          </GoogleMap>
        </div>
      )}
    </div>
  );
};

export default CourseMap; 